import { ApprovalStatus, BaseTkEntry, SignatureState, tUserRole } from "../../common/types"
import { tContext, WeeklyViewSourceData } from "./types"
import { iTimekeepingStatus, iTimelineEntryVersionSummary, tEntitySelection } from "../../cached-data/types"
import {
    colorAttentionBlue60,
    colorAttentionRed50,
    colorAttentionYellow50,
    colorFunctionalGray50,
    colorFunctionalGray70,
    IconCicoDeficit,
    IconCicoDefault,
    IconCicoExcess,
} from "@rhumbix/rmbx_design_system_web"
import { getObjectId } from "../../common/ag-grid-ts-utils"
import { TIMELINE_ENTRY_TYPE } from "../../common/constants"

const entriesAreAllSynced = (tkes: Pick<BaseTkEntry, "status">[]) => tkes.length > 0 && tkes.every(tke => tke.status === ApprovalStatus.SYNCED)

const sourceDataIsAllSynced = (sourceData: WeeklyViewSourceData) => {
    const { absences = [], employeeEntries = [], timekeepingEntries = [], ewsStartStopTimes = [] } = sourceData
    return entriesAreAllSynced([...absences, ...employeeEntries, ...timekeepingEntries, ...ewsStartStopTimes])
}

export const selectedCellHasAllSyncedData = (context: tContext): boolean => {
    // During a Fancy Search, selectedCellData is briefly an empty array (due to a table re-render?).
    // In any case, avoid failing.
    if (Array.isArray(context.selectedCellData)) {
        return false
    }
    return sourceDataIsAllSynced(context.selectedCellData)
}

const entriesUnableToTransition = (tkes: Pick<BaseTkEntry, "status">[], userRole: tUserRole, statuses?: iTimekeepingStatus[]) => {
    if (tkes.length > 0) {
        if (statuses) {
            return tkes.some(tke => {
                if (!tke.status) return false
                const statusObj = statuses.find(s => {
                    const status = typeof tke.status === "object" ? tke.status.name : tke.status
                    return s.name === status
                })
                return !statusObj?.can_edit_cell || !statusObj.can_edit_cell.includes(userRole)
            })
        }
    }
    return false
}

const sourceDataUnableToTransition = (sourceData: WeeklyViewSourceData, userRole: tUserRole, statuses?: iTimekeepingStatus[]) => {
    const { absences = [], employeeEntries = [], timekeepingEntries = [], ewsStartStopTimes = [] } = sourceData
    return entriesUnableToTransition([...absences, ...employeeEntries, ...timekeepingEntries, ...ewsStartStopTimes], userRole, statuses)
}

/**
 * Determines whether the current table context or selected cell can transition
 * @param context A context containing the data for the selected cell/table
 * @returns
 */
export const selectedCellUnableToTransition = (context: tContext): boolean => {
    // During a Fancy Search, selectedCellData is briefly an empty array (due to a table re-render?).
    // In any case, avoid failing.
    if (Array.isArray(context.selectedCellData)) {
        return false
    }
    const tkStatuses = context.referenceableData?.timekeepingStatuses ? Object.values(context.referenceableData.timekeepingStatuses) : undefined
    return sourceDataUnableToTransition(context.selectedCellData, context.currentUser.user_role, tkStatuses)
}

export const formatTimeDiff = (timeDiff: number): string => (timeDiff > 0 ? `+${timeDiff.toFixed(2)}` : timeDiff === 0 ? "+0" : timeDiff.toFixed(2))

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getCicoIcon = (timeDiff: number) => (timeDiff === 0 ? IconCicoDefault : timeDiff > 0 ? IconCicoExcess : IconCicoDeficit)

export const getCicoIconColor = (timeDiff: number, totalTime = 0): string => (timeDiff >= 0 ? colorAttentionBlue60 : totalTime ? colorAttentionYellow50 : colorFunctionalGray50)

/**
 * @param signatureStates The set of signature states of every TK entry underlying the cell
 * @returns The highest-severity state, or null if the set is empty
 */
export const getSignatureIconState = (signatureStates: Set<SignatureState | undefined>): SignatureState | null => {
    if (signatureStates.has("SIGNED_INVALID")) return "SIGNED_INVALID"
    if (signatureStates.has("MISSING")) return "MISSING"
    if (signatureStates.has("SIGNED")) return "SIGNED"
    return null
}

const SIGNATURE_ICON_COLOR_BY_STATE: Record<SignatureState, string> = {
    SIGNED_INVALID: colorAttentionRed50,
    MISSING: colorAttentionYellow50,
    SIGNED: colorFunctionalGray70,
}

export const getSignatureIconColor = (signatureStates: Set<SignatureState | undefined>): string | null => {
    const state = getSignatureIconState(signatureStates)
    return state ? SIGNATURE_ICON_COLOR_BY_STATE[state] : null
}

/**
 * Find out whether cico is enabled from any locked columns which would contain project data
 * (timecard, project, or cost code). We default to true if it can't find the setting.
 * @param context The ag-grid table's context
 */
export const cicoEnabledFromLockedColumns = (context: tContext): boolean => {
    const { groupKeyInfo, referenceableData } = context
    const projectLockedColumn = groupKeyInfo.find(lc => lc.colDef.field === "/project")
    const timecardLockedColumn = groupKeyInfo.find(lc => lc.colDef.field === "/work_shift_id")
    const costCodeLockedColumn = groupKeyInfo.find(lc => lc.colDef.field === "/cost_code")
    if (!projectLockedColumn && !timecardLockedColumn && !costCodeLockedColumn) return true

    if (projectLockedColumn) return referenceableData.projects[getObjectId(projectLockedColumn.value)]?.cico_settings?.enabled ?? true

    // Look out for the tempId from the create timecard flow
    if (timecardLockedColumn && !timecardLockedColumn.value.tempId) {
        const workShift = referenceableData.workShifts[getObjectId(timecardLockedColumn.value)]
        if (workShift?.projects) return workShift.projects.some(pId => referenceableData.projects[pId]?.cico_settings?.enabled)
    }
    if (costCodeLockedColumn) {
        let project = referenceableData.costCodes[getObjectId(costCodeLockedColumn.value)]?.project
        if (!project) project = costCodeLockedColumn.value?.project
        if (project && referenceableData.projects[getObjectId(project)]) return referenceableData.projects[getObjectId(project)]?.cico_settings?.enabled ?? true
    }
    return true
}

/**
 * Fetch the rounded CI/CO time for each employee, broken up by project from a set of timeline entry versions.
 * @param cicoEntries The timeline entry versions to calculate the CI/CO time for
 * @param referenceableData The referenceable data to use to get the projects
 * @param timekeepingIncrement The timekeeping increment to use to round the CI/CO time
 * @returns The rounded CI/CO time for each employee, broken up by project
 */
export const getEmployeeProjectCico = (cicoEntries: iTimelineEntryVersionSummary[], referenceableData: tEntitySelection, timekeepingIncrement: number): Record<number, Record<number, number>> => {
    const employeeProjectBreakdown: Record<number, Record<number, number>> = cicoEntries.reduce((acc, el) => {
        if (el.entry_type === TIMELINE_ENTRY_TYPE.START_MEAL || el.entry_type === TIMELINE_ENTRY_TYPE.CLOCK_OUT) {
            return acc
        }
        if (!(el.employee in acc)) {
            acc[el.employee] = {}
        }
        if (!(el.project in acc[el.employee])) {
            acc[el.employee][el.project] = 0
        }
        acc[el.employee][el.project] += el.duration

        return acc
    }, {} as Record<number, Record<number, number>>)

    for (const employee in employeeProjectBreakdown) {
        for (const projectId in employeeProjectBreakdown[employee]) {
            const project = referenceableData.projects[projectId]
            const duration = employeeProjectBreakdown[employee][projectId]
            const roundingIncrement = project.cico_settings?.rounding_increment
            employeeProjectBreakdown[employee][projectId] =
                roundingIncrement !== undefined && duration % timekeepingIncrement >= roundingIncrement
                    ? Math.ceil(duration / timekeepingIncrement) * timekeepingIncrement
                    : Math.floor(duration / timekeepingIncrement) * timekeepingIncrement
        }
    }
    return employeeProjectBreakdown
}

/**
 * Normalizes an array of strings or keyed objects to an array of objects with a specified key.
 * Used to ensure selectors always receive objects with the expected property.
 *
 * @param arr - Array of strings or objects with the specified key, or undefined/null
 * @param key - The property name to use for string values (defaults to "name")
 * @returns Array of objects with the specified key, or empty array if input is falsy
 *
 * @example
 * normalizeToObjectArray(["foo", "bar"]) // [{ name: "foo" }, { name: "bar" }]
 * normalizeToObjectArray(["A", "B"], "group") // [{ group: "A" }, { group: "B" }]
 */
export const normalizeToObjectArray = <Key extends string = "name">(arr: Array<string | Record<Key, string>> | undefined | null, key: Key = "name" as Key): Record<Key, string>[] => {
    if (!arr || !Array.isArray(arr)) return []
    return arr.map(item => (typeof item === "string" ? ({ [key]: item } as Record<Key, string>) : item))
}

/**
 * Formats a value that may be a string, object, or array of either into a display string.
 * Arrays are joined with ", ". Single values are returned as-is or extracted by field.
 * AG-Grid cell renderers pass arrays for cell display, and single items for selector pills.
 *
 * @param value - A string, keyed object, or array of either; falsy values return ""
 * @param field - The property name to extract from objects (defaults to "name")
 * @returns Comma-separated string representation of the value(s)
 */
export const stringOrObjectArrayFormatter = (value: string | Record<string, any> | string[] | Record<string, any>[] | undefined | null, field: string = "name"): string => {
    if (!value) return ""
    // Handle single item (used by selector pills for individual selected values)
    if (!Array.isArray(value)) {
        return typeof value === "string" ? value : value?.[field] || ""
    }
    // Handle array (used by cell display)
    return value
        .map((item: string | Record<string, any>) => (typeof item === "string" ? item : item?.[field] || ""))
        .filter(Boolean)
        .join(", ")
}

/**
 * Extracts numeric IDs from an array that may contain raw numbers or objects with an id property.
 *
 * @param arr - Array of numbers or objects with an id property, or undefined
 * @returns Array of numeric IDs, or empty array if input is undefined
 */
export const toIds = (arr: Array<number | { id: number }> | undefined): number[] => (arr ? arr.map(item => getObjectId(item)) : [])

/**
 * Extracts string values from an array that may contain raw strings or keyed objects.
 *
 * @param arr - Array of strings or objects with the specified key, or undefined
 * @param key - The property name to extract from objects (defaults to "name")
 * @returns Array of string values, or empty array if input is undefined
 */
export const toKeys = (arr: Array<string | Record<string, string>> | undefined, key: string = "name"): string[] =>
    arr && Array.isArray(arr) ? arr.map(item => (typeof item === "string" ? item : item?.[key])) : []
